import type { IHosAvailability, IHosPreciseAvailability, IValidation, IWorkdaySummary } from "./helpers";
import { msToTimespan, timeSpanToMs } from "./helpers";
import type { IViolation } from "../interfaces/violation";

export interface INonSplitResult {
    availability: IHosAvailability;
    preciseAvailability?: IHosPreciseAvailability;
    violations: IViolation[];
}

export interface IDrivingInterval {
    start: Date;
    end: Date;
}

/**
 * Projects the "without split sleeper benefit" view of an already-calculated validation result.
 *
 * The engine always calculates optimistically: an in-progress qualifying sleeper period "freezes"
 * time onto the current workday's end. This projection removes ONLY that speculative freeze.
 * Time EARNED by a completed pairing is preserved: past resets live in closed workdays (history is
 * never recalculated), and the completed pairing's later-leg exclusion on the current workday is
 * kept via earnedSplitExclusionMs — only the speculative excess above it is stripped.
 *
 * `availabilityFloorMs` is the Emergency Response floor (MYG-139401), zero when no exemption is
 * running. Stripping the freeze from an already-floored value would put this view below the floor
 * the driver is shown everywhere else, so the floor is re-applied afterwards. Exempt time is kept
 * out of the derived violations by the caller, which clips `currentWorkdayDrivingIntervals` before
 * passing them in — this function stays free of any exemption knowledge.
 */
export function computeNonSplitResult(
    validation: IValidation,
    workdayMaximumMs: number | undefined,
    workdayExtendedMs: number,
    currentWorkdayDrivingIntervals: IDrivingInterval[] = [],
    earnedSplitExclusionMs: number = 0,
    availabilityFloorMs: number = 0
): INonSplitResult {
    const unchanged = copyOf(validation);

    if (workdayMaximumMs === undefined) {
        return unchanged;
    }
    const currentWorkday = inProgressWorkdayOf(validation);
    if (currentWorkday === undefined) {
        return unchanged;
    }
    const nonSplitWorkdayLengthMs = workdayMaximumMs + workdayExtendedMs + earnedSplitExclusionMs;
    const frozenExtensionMs = frozenExtensionOf(currentWorkday, nonSplitWorkdayLengthMs);
    if (frozenExtensionMs <= 0) {
        return unchanged;
    }
    const stripped = stripFrozenExtension(unchanged, frozenExtensionMs, availabilityFloorMs);
    const nonSplitWorkdayEnd = new Date(currentWorkday.fromDate!.getTime() + nonSplitWorkdayLengthMs);
    stripped.violations = withDerivedWorkdayViolations(stripped.violations, nonSplitWorkdayEnd, currentWorkdayDrivingIntervals);
    return stripped;
}

function copyOf(validation: IValidation): INonSplitResult {
    return {
        availability: { ...validation.availability },
        preciseAvailability: validation.preciseAvailability === undefined ? undefined : { ...validation.preciseAvailability },
        violations: [...validation.violations]
    };
}

function inProgressWorkdayOf(validation: IValidation): IWorkdaySummary | undefined {
    const lastWorkday = validation.workdaySummaries?.[validation.workdaySummaries.length - 1];
    if (lastWorkday === undefined || lastWorkday.isInProgress !== true) {
        return undefined;
    }
    if (lastWorkday.fromDate === undefined || lastWorkday.toDate === undefined) {
        return undefined;
    }
    return lastWorkday;
}

function frozenExtensionOf(currentWorkday: IWorkdaySummary, nonSplitWorkdayLengthMs: number): number {
    const nonSplitWorkdayEnd = currentWorkday.fromDate!.getTime() + nonSplitWorkdayLengthMs;
    return currentWorkday.toDate!.getTime() - nonSplitWorkdayEnd;
}

function stripFrozenExtension(result: INonSplitResult, frozenExtensionMs: number, availabilityFloorMs: number): INonSplitResult {
    // MYG-139401: the values arriving here are already floored when an Emergency Response
    // exemption is running, so subtracting the frozen extension would produce floor − extension —
    // neither the floored number the driver is shown nor the real one. Re-applying the floor
    // after the subtraction keeps this view on the same clock as the primary one.
    const strippedWorkdayMs = Math.max(reduceTimespan(result.availability.workday, frozenExtensionMs), availabilityFloorMs);
    // Driving needs no floor of its own: it arrives already floored and is only ever capped to the
    // workday, which is floored above, so the smaller of the two is still at or above the floor.
    const cappedDrivingMs = capTimespan(result.availability.driving, strippedWorkdayMs);

    result.availability.workday = msToTimespan(strippedWorkdayMs);
    result.availability.driving = msToTimespan(cappedDrivingMs);
    if (result.preciseAvailability !== undefined) {
        result.preciseAvailability.workday = strippedWorkdayMs;
        result.preciseAvailability.driving = cappedDrivingMs;
    }
    return result;
}

/**
 * Mirrors the engine's Workday violation rule (hosCalculator calculateWorkdayViolation):
 * a Workday violation exists only while the driver is DRIVING beyond the workday end.
 * Here the end is the non-split end, so driving inside the frozen extension becomes a violation.
 * Engine violations already covered by a derived one are superseded to avoid double reporting.
 */
function withDerivedWorkdayViolations(violations: IViolation[], nonSplitWorkdayEnd: Date, drivingIntervals: IDrivingInterval[]): IViolation[] {
    const derived = drivingIntervals
        .filter(drivingInterval => drivingInterval.end.getTime() > nonSplitWorkdayEnd.getTime())
        .map(drivingInterval => workdayViolationFor(drivingInterval, nonSplitWorkdayEnd));

    const notSuperseded = violations.filter(violation => !isCoveredByDerived(violation, derived));
    return [...notSuperseded, ...derived];
}

function workdayViolationFor(drivingInterval: IDrivingInterval, nonSplitWorkdayEnd: Date): IViolation {
    const violationStart = Math.max(drivingInterval.start.getTime(), nonSplitWorkdayEnd.getTime());
    return {
        type: "Workday",
        fromDate: new Date(violationStart),
        toDate: drivingInterval.end
    };
}

function isCoveredByDerived(violation: IViolation, derived: IViolation[]): boolean {
    if (violation.type !== "Workday") {
        return false;
    }
    return derived.some(derivedViolation => describesSameOffence(derivedViolation, violation));
}

function describesSameOffence(derivedViolation: IViolation, engineViolation: IViolation): boolean {
    if (derivedViolation.toDate === undefined) {
        return false;
    }
    const startsWithinDerived = engineViolation.fromDate.getTime() >= derivedViolation.fromDate.getTime();
    if (!startsWithinDerived) {
        return false;
    }
    // An engine violation still OPEN (no toDate) is the same driving the derived one was built
    // from — derived intervals run up to "now", so an open record can never extend past them.
    if (engineViolation.toDate === undefined) {
        return true;
    }
    return engineViolation.toDate.getTime() <= derivedViolation.toDate.getTime();
}

function reduceTimespan(timespan: string | undefined, reductionMs: number): number {
    const originalMs = timespan === undefined ? 0 : timeSpanToMs(timespan);
    return Math.max(0, originalMs - reductionMs);
}

function capTimespan(timespan: string | undefined, maximumMs: number): number {
    const originalMs = timespan === undefined ? 0 : timeSpanToMs(timespan);
    return Math.min(originalMs, maximumMs);
}
